import { isNumeric } from './lensChart';

/**
 * Models for the two card shapes a series-and-axis chart model cannot express.
 *
 * A heatmap is two categorical axes and one measure per cell, not a set of
 * series, so buildChartModel would pivot it into one series per row value and
 * lose the grid. A distribution is many observations per category reduced to
 * five statistics, which no per-row model carries either.
 *
 * Both are computed here rather than inside a renderer so ECharts, Vega-Lite
 * and any hand-rolled fallback draw the same numbers.
 */

export type LensMatrixCell = { x: string; y: string; value: number | null };

export type LensMatrixModel = {
  xValues: Array<string>;
  yValues: Array<string>;
  cells: Array<LensMatrixCell>;
  min: number;
  max: number;
};

/** Distinct values in first-seen order, so a caller can impose a meaningful order by row order. */
function distinct(rows: Array<Record<string, unknown>>, column: string): Array<string> {
  const seen = new Set<string>();
  const values: Array<string> = [];
  rows.forEach((row) => {
    const value = String(row[column] ?? '');
    if (!seen.has(value)) {
      seen.add(value);
      values.push(value);
    }
  });
  return values;
}

export function buildMatrixModel(
  rows: Array<Record<string, unknown>>,
  xColumn: string,
  yColumn: string,
  valueColumn: string,
): LensMatrixModel | null {
  if (!rows.length) {
    return null;
  }

  const xValues = distinct(rows, xColumn);
  const yValues = distinct(rows, yColumn);
  const byKey = new Map<string, number>();

  rows.forEach((row) => {
    if (isNumeric(row[valueColumn])) {
      byKey.set(`${String(row[xColumn] ?? '')} ${String(row[yColumn] ?? '')}`, Number(row[valueColumn]));
    }
  });

  const cells: Array<LensMatrixCell> = [];
  yValues.forEach((y) => {
    xValues.forEach((x) => {
      const value = byKey.get(`${x} ${y}`);
      cells.push({ x, y, value: value === undefined ? null : value });
    });
  });

  const present = cells.map((cell) => cell.value).filter((value): value is number => value !== null);
  if (!present.length) {
    return null;
  }

  return { xValues, yValues, cells, min: Math.min(...present), max: Math.max(...present) };
}

export type LensDistribution = {
  category: string;
  min: number;
  q1: number;
  median: number;
  q3: number;
  max: number;
  /** Points outside 1.5 IQR of the box, which the whiskers deliberately exclude. */
  outliers: Array<number>;
  count: number;
};

/** Linear interpolation between order statistics, matching what BigQuery's PERCENTILE_CONT returns. */
function quantile(sorted: Array<number>, fraction: number): number {
  if (sorted.length === 1) {
    return sorted[0];
  }
  const position = (sorted.length - 1) * fraction;
  const lower = Math.floor(position);
  const upper = Math.ceil(position);
  if (lower === upper) {
    return sorted[lower];
  }
  return sorted[lower] + (sorted[upper] - sorted[lower]) * (position - lower);
}

export function buildDistributions(
  rows: Array<Record<string, unknown>>,
  categoryColumn: string,
  valueColumn: string,
): Array<LensDistribution> {
  const grouped = new Map<string, Array<number>>();
  rows.forEach((row) => {
    if (!isNumeric(row[valueColumn])) {
      return;
    }
    const category = String(row[categoryColumn] ?? '');
    const bucket = grouped.get(category);
    if (bucket) {
      bucket.push(Number(row[valueColumn]));
    } else {
      grouped.set(category, [Number(row[valueColumn])]);
    }
  });

  return [...grouped.entries()].map(([category, values]) => {
    const sorted = [...values].sort((a, b) => a - b);
    const q1 = quantile(sorted, 0.25);
    const median = quantile(sorted, 0.5);
    const q3 = quantile(sorted, 0.75);
    const fence = (q3 - q1) * 1.5;
    // Whiskers stop at the furthest observation inside the fence, never at the
    // fence itself: drawing to the fence invents a value nothing reached.
    const inside = sorted.filter((value) => value >= q1 - fence && value <= q3 + fence);
    return {
      category,
      min: inside.length ? inside[0] : sorted[0],
      q1,
      median,
      q3,
      max: inside.length ? inside[inside.length - 1] : sorted[sorted.length - 1],
      outliers: sorted.filter((value) => value < q1 - fence || value > q3 + fence),
      count: sorted.length,
    };
  });
}

/** The full extent a distribution axis has to cover, outliers included. */
export function distributionExtent(distributions: Array<LensDistribution>): { min: number; max: number } {
  const values = distributions.flatMap((entry) => [entry.min, entry.max, ...entry.outliers]);
  return values.length ? { min: Math.min(...values), max: Math.max(...values) } : { min: 0, max: 0 };
}
